import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Globe, Users, Coins, TrendingUp, AlertTriangle,
  Activity, RefreshCw, ExternalLink, Zap, ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';

interface PlatformOverview {
  activeWorlds: number;
  totalPlayerAccounts: number;
  cbReserveBalance: string;
  cbTotalSupply: string;
  cbCirculatingSupply: string;
  activeAnomalyFlags: number;
}

function formatUkc(raw: string): string {
  const n = parseFloat(raw);
  if (isNaN(n)) return '0';
  if (n >= 1e12) return `${(n / 1e12).toFixed(3)}T`;
  if (n >= 1e9)  return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6)  return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3)  return `${(n / 1e3).toFixed(2)}K`;
  return n.toLocaleString();
}

/* Fake sparkline data so cards look alive even before live data */
function makeSpark(base: number, points = 14) {
  let v = base;
  return Array.from({ length: points }, () => {
    v += (Math.random() - 0.47) * v * 0.06;
    return Math.max(0, v);
  });
}

/* Pure SVG sparkline — no external deps, no global ID conflicts */
function Sparkline({ data, color }: { data: number[]; color: string }) {
  const W = 200;
  const H = 36;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const xs = data.map((_, i) => (i / (data.length - 1)) * W);
  const ys = data.map((v) => H - ((v - min) / range) * (H * 0.85) - H * 0.05);
  const linePoints = xs.map((x, i) => `${x},${ys[i]}`).join(' ');
  const areaPoints = `0,${H} ${linePoints} ${W},${H}`;
  // Use inline style for fill so gradient id is scoped per instance via a data-uri trick
  const gradId = `sg-${color.replace('#', '')}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 36 }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#${gradId})`} />
      <polyline points={linePoints} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  color: 'cyan' | 'purple' | 'green' | 'amber' | 'red';
  sparkBase?: number;
  delay?: number;
}

const colorMap = {
  cyan:   { border: 'rgba(0,212,255,0.25)',   bg: 'rgba(0,212,255,0.07)',   text: 'text-cyan-400',   stroke: '#00d4ff', icon: 'text-cyan-400' },
  purple: { border: 'rgba(124,58,237,0.3)',   bg: 'rgba(124,58,237,0.08)',  text: 'text-purple-400', stroke: '#7c3aed', icon: 'text-purple-400' },
  green:  { border: 'rgba(16,185,129,0.25)',  bg: 'rgba(16,185,129,0.07)',  text: 'text-emerald-400', stroke: '#10b981', icon: 'text-emerald-400' },
  amber:  { border: 'rgba(245,158,11,0.25)',  bg: 'rgba(245,158,11,0.07)',  text: 'text-amber-400',  stroke: '#f59e0b', icon: 'text-amber-400' },
  red:    { border: 'rgba(239,68,68,0.3)',    bg: 'rgba(239,68,68,0.08)',   text: 'text-red-400',    stroke: '#ef4444', icon: 'text-red-400' },
};

function StatCard({ label, value, sub, icon: Icon, color, sparkBase = 100, delay = 0 }: StatCardProps) {
  const c = colorMap[color];
  const spark = makeSpark(sparkBase);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4, ease: 'easeOut' }}
      className="glass-card relative overflow-hidden p-5"
      style={{ borderColor: c.border }}
    >
      {/* top gradient bar */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${c.stroke}60, transparent)` }} />

      {/* icon + label */}
      <div className="flex items-start justify-between mb-3">
        <div className="rounded-xl p-2" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
          <Icon className={`w-4 h-4 ${c.icon}`} />
        </div>
        <p className="text-[10px] font-medium uppercase tracking-widest text-slate-600 text-right">{label}</p>
      </div>

      <p className={`text-2xl font-bold count-up ${c.text} mb-0.5`}>{value}</p>
      {sub && <p className="text-xs text-slate-600 mb-3">{sub}</p>}

      {/* sparkline — self-contained SVG, no external deps, no global ID issues */}
      <div className="mt-3 overflow-hidden" style={{ height: 36 }}>
        <Sparkline data={spark} color={c.stroke} />
      </div>
    </motion.div>
  );
}

function SkeletonCard() {
  return (
    <div className="glass-card p-5 space-y-3">
      <div className="shimmer h-8 w-8 rounded-xl" />
      <div className="shimmer h-7 w-24 rounded-lg" />
      <div className="shimmer h-4 w-16 rounded" />
      <div className="shimmer h-10 w-full rounded-lg mt-2" />
    </div>
  );
}

const ACCOUNT_LABELS: Record<string, string> = {
  P2E_POOL: 'P2E Pool', OWNER_REVENUE: 'Revenue', CB_ALLOCATION: 'CB Allocation', FUTURE_RESERVE: 'Future Reserve',
};
const ACCOUNT_COLORS: Record<string, string> = {
  P2E_POOL: 'text-emerald-400', OWNER_REVENUE: 'text-purple-400', CB_ALLOCATION: 'text-cyan-400', FUTURE_RESERVE: 'text-amber-400',
};

function PartnerWorldCard({ world }: { world: Record<string, unknown> }) {
  const worldId = world['id'] as string;
  const { data: allBalances, isError: balancesError } = useQuery<{
    worldId: string; worldName: string; currencySymbol: string; currencyName: string;
    nativeAccounts: { accountType: string; balance: string; currency: string }[];
    foreignBalances: { id: string; sourceWorldId: string; sourceWorldName: string; currency: string; balance: string; updatedAt: string }[];
  } | null>({
    queryKey: ['world-all-balances', worldId],
    queryFn: () => api.get(`/central-bank/worlds/${worldId}/all-balances`).then((r) => r.data),
    staleTime: 30_000,
    retry: 1,
  });
  const { data: analytics, isError: analyticsError } = useQuery<{ wsfValue?: string; anomalyCount?: number } | null>({
    queryKey: ['world-metrics', worldId],
    queryFn: () => api.get(`/analytics/worlds/${worldId}/metrics`).then((r) => r.data),
    staleTime: 30_000,
    retry: 1,
  });
  const { data: anomalies = [], isError: anomaliesError } = useQuery<unknown[]>({
    queryKey: ['world-anomalies', worldId],
    queryFn: () => api.get(`/analytics/worlds/${worldId}/anomalies`).then((r) => r.data),
    staleTime: 30_000,
    retry: 1,
  });
  const hasSubError = balancesError || analyticsError || anomaliesError;

  const status = world['status'] as string;
  const wsf = (world as Record<string, Record<string, string>>)['scalingConfig']?.['currentWsf'] ?? analytics?.['wsfValue'] ?? '—';
  const anomalyCount = anomalies.length;
  const nativeAccounts = allBalances?.nativeAccounts ?? [];
  const foreignBalances = (allBalances?.foreignBalances ?? []).filter((fb) => parseFloat(fb.balance) > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6 space-y-5"
      style={{ borderColor: 'rgba(0,212,255,0.12)' }}
    >
      {/* World header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/20 flex items-center justify-center">
            <Globe className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">{world['name'] as string}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-mono text-xs text-slate-500 bg-white/[0.04] px-1.5 py-0.5 rounded">
                {world['currencySymbol'] as string}
              </span>
              <span className={`text-[10px] font-medium ${status === 'ACTIVE' ? 'text-emerald-400' : status === 'PENDING' ? 'text-amber-400' : 'text-slate-500'}`}>
                {status}
              </span>
            </div>
          </div>
        </div>
        <Link to={`/worlds/${worldId}`} className="flex items-center gap-1 text-xs text-cyan-500 hover:text-cyan-300 transition-colors font-medium">
          Details <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* Sub-query error indicator */}
      {hasSubError && (
        <div className="flex items-center gap-1.5 text-[10px] text-amber-400 bg-amber-500/8 border border-amber-500/15 rounded-lg px-2.5 py-1.5">
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          Some world data could not be loaded
        </div>
      )}

      {/* WSF + anomaly row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/[0.02] border border-white/5 rounded-xl px-3 py-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Zap className="w-3 h-3 text-amber-400" />
            <p className="text-[10px] text-slate-600 uppercase tracking-wider">WSF Factor</p>
          </div>
          <p className="text-xl font-bold text-amber-400">{wsf}</p>
        </div>
        <div className="bg-white/[0.02] border border-white/5 rounded-xl px-3 py-3">
          <div className="flex items-center gap-1.5 mb-1">
            <AlertTriangle className="w-3 h-3 text-red-400" />
            <p className="text-[10px] text-slate-600 uppercase tracking-wider">Anomalies</p>
          </div>
          <p className={`text-xl font-bold ${anomalyCount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
            {anomalyCount > 0 ? anomalyCount : '✓ Clear'}
          </p>
        </div>
      </div>

      {/* Native account balances */}
      {nativeAccounts.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-2">Account Balances</p>
          <div className="grid grid-cols-2 gap-2">
            {nativeAccounts.map((acc) => (
              <div key={acc.accountType} className="flex items-center justify-between bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2">
                <span className="text-[10px] text-slate-600">{ACCOUNT_LABELS[acc.accountType] ?? acc.accountType}</span>
                <div className="flex items-center gap-1">
                  <span className="font-mono text-[10px] text-slate-600">{acc.currency}</span>
                  <span className={`font-mono text-xs font-semibold ${ACCOUNT_COLORS[acc.accountType] ?? 'text-slate-400'}`}>
                    {Number(acc.balance).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Foreign currencies held */}
      {foreignBalances.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-2">Foreign Currencies Held</p>
          <div className="grid grid-cols-2 gap-2">
            {foreignBalances.map((fb) => (
              <div key={fb.id} className="flex items-center justify-between bg-white/[0.02] border border-teal-500/10 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <span className="text-[10px] font-mono text-teal-400">{fb.currency}</span>
                  <span className="block text-[9px] text-slate-700 truncate">{fb.sourceWorldName}</span>
                </div>
                <span className="font-mono text-xs font-semibold text-teal-400 ml-2 shrink-0">
                  {Number(fb.balance).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

function PartnerOverview() {
  const { data: worlds = [], isLoading, refetch, isFetching } = useQuery<Record<string, unknown>[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
    refetchInterval: 60_000,
  });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-xl font-bold text-white">My Worlds Overview</h2>
          <p className="text-sm text-slate-600 mt-0.5">Financial status of your assigned worlds</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 transition-colors px-3 py-1.5 rounded-lg border border-white/5 hover:border-cyan-500/20 bg-white/[0.02]"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="glass-card p-6 space-y-3">
              <div className="shimmer h-10 w-48 rounded-xl" />
              <div className="shimmer h-16 w-full rounded-xl" />
              <div className="shimmer h-24 w-full rounded-xl" />
            </div>
          ))}
        </div>
      ) : worlds.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Globe className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No worlds assigned to your account</p>
          <p className="text-slate-700 text-xs mt-1">Contact your administrator to get worlds assigned</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {worlds.map((w) => <PartnerWorldCard key={w['id'] as string} world={w} />)}
        </div>
      )}
    </div>
  );
}

export function PlatformOverviewPage() {
  const role = useAuthStore((s) => s.role);
  if (role === 'WORLD_OWNER') return <PartnerOverview />;

  return <AdminOverviewPage />;
}

function AdminOverviewPage() {
  const { data, isLoading, error, dataUpdatedAt, refetch, isFetching } = useQuery<PlatformOverview>({
    queryKey: ['platform-overview'],
    queryFn: () => api.get('/analytics/platform/overview').then((r) => r.data),
    refetchInterval: 60_000,
  });

  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : null;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-xl font-bold text-white">Platform Overview</h2>
          <p className="text-sm text-slate-600 mt-0.5">Real-time financial system metrics</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-slate-600">Updated {lastUpdated}</span>
          )}
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 transition-colors px-3 py-1.5 rounded-lg border border-white/5 hover:border-cyan-500/20 bg-white/[0.02]"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 flex items-center gap-2 text-amber-300 text-sm bg-amber-500/8 border border-amber-500/20 rounded-xl px-4 py-3"
        >
          <AlertTriangle className="w-4 h-4" />
          Analytics service unavailable — showing cached data
        </motion.div>
      )}

      {/* Stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          <>
            <StatCard label="Active Worlds"    value={data?.activeWorlds ?? 0}                          icon={Globe}         color="cyan"   sparkBase={8}    delay={0.0} />
            <StatCard label="Player Accounts"  value={(data?.totalPlayerAccounts ?? 0).toLocaleString()} icon={Users}         color="purple" sparkBase={4200} delay={0.07} />
            <StatCard label="Circulating"       value={formatUkc(data?.cbCirculatingSupply ?? '0')}      icon={Coins}         color="green"  sparkBase={50000} delay={0.14} sub="UKC issued" />
            <StatCard label="Total Supply"       value={formatUkc(data?.cbTotalSupply ?? '0')}             icon={TrendingUp}    color="amber"  sparkBase={5e9}   delay={0.21} sub="UKC" />
            <StatCard
              label="Anomaly Flags"
              value={data?.activeAnomalyFlags ?? 0}
              icon={AlertTriangle}
              color={data?.activeAnomalyFlags ? 'red' : 'green'}
              sparkBase={2}
              delay={0.28}
              sub={data?.activeAnomalyFlags ? '⚠ Requires attention' : 'All clear'}
            />
          </>
        )}
      </div>

      {/* Status row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* System health */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          className="glass-card p-6"
        >
          <div className="flex items-center gap-2 mb-5">
            <Activity className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-semibold text-white">System Health</h3>
          </div>
          <div className="space-y-3">
            {[
              { name: 'API Gateway',        port: 7501, color: 'bg-emerald-400' },
              { name: 'Central Bank',       port: 7504, color: 'bg-emerald-400' },
              { name: 'Analytics',          port: 7509, color: error ? 'bg-amber-400' : 'bg-emerald-400' },
              { name: 'Exchange Service',   port: 7506, color: 'bg-emerald-400' },
              { name: 'Ledger',             port: 7503, color: 'bg-emerald-400' },
            ].map((svc) => (
              <div key={svc.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className={`pulse-dot ${svc.color}`} />
                  <span className="text-sm text-slate-300">{svc.name}</span>
                </div>
                <span className="font-mono text-xs text-slate-600">:{svc.port}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Quick links */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.42, duration: 0.4 }}
          className="glass-card p-6"
        >
          <div className="flex items-center gap-2 mb-5">
            <ExternalLink className="w-4 h-4 text-purple-400" />
            <h3 className="text-sm font-semibold text-white">Quick Access</h3>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Admin API Docs',       href: '/docs',                    note: 'Admin REST explorer' },
              { label: 'Grafana Dashboards',   href: `${window.location.protocol}//${window.location.hostname}:7561`,  note: 'Metrics & charts' },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between px-4 py-3 rounded-xl border border-white/5 hover:border-cyan-500/20 bg-white/[0.02] hover:bg-cyan-500/5 transition-all group"
              >
                <div>
                  <div className="text-sm text-slate-300 group-hover:text-white transition-colors">{link.label}</div>
                  <div className="text-xs text-slate-600">{link.note}</div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-slate-700 group-hover:text-cyan-400 transition-colors" />
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

